import React, { useEffect, useRef, useState } from 'react';
import { X, AlertTriangle, ArrowRight, Dices, Pencil, FileText, Loader2 } from 'lucide-react';
import { AppIcon } from './Logo';
import { Avatar, randomAvatar } from './Avatar';
import { AvatarCustomizer } from './AvatarCustomizer';
import { guestOrNew, saveGuest, type GuestIdentity } from '../lib/guest';
import { validateUsername } from '../lib/profanity';
import { LegalLink } from './ProfileFields';
import type { AvatarConfig } from '../types';
import type { ExamPublicInfo } from '../../shared/exams';
import { api } from '../lib/api';

interface Props {
  error?: string;
  /** `true` cuando nadie ha iniciado sesión: entonces se pide identidad de invitado. */
  asGuest: boolean;
  /** PIN ya rellenado, cuando se llega desde la sala de espera. */
  initialPin?: string;
  onJoin: (pin: string, guest?: GuestIdentity) => void;
  /** El PIN es de un examen: se va a su pantalla (allí se piden nombre y apellido). */
  onExam: (pin: string) => void;
  onClose: () => void;
}

const PIN_LENGTH = 6;

/**
 * Entrada del PIN, y —para quien no tiene cuenta— apodo y avatar de la partida.
 *
 * Seis casillas en lugar de un campo suelto: se lee de un vistazo cuántos
 * dígitos faltan, y el PIN se proyecta en pantalla con ese mismo formato. El
 * teclado numérico se pide explícitamente para que en el móvil no salga el
 * alfabético.
 *
 * La identidad de invitado se guarda en `sessionStorage` (ver `lib/guest`): se
 * conserva si la pestaña se recarga a mitad de partida y desaparece al cerrar
 * el navegador, de modo que el siguiente que use ese ordenador empiece limpio.
 */
export const JoinPinModal: React.FC<Props> = ({ error, asGuest, initialPin, onJoin, onExam, onClose }) => {
  const [digits, setDigits] = useState<string[]>(() =>
    Array.from({ length: PIN_LENGTH }, (_, i) => initialPin?.replace(/\D/g, '')[i] ?? ''),
  );
  const [guest, setGuest] = useState<GuestIdentity>(guestOrNew);
  const [customizing, setCustomizing] = useState(false);
  const inputsRef = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    inputsRef.current[0]?.focus();
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (customizing) setCustomizing(false);
        else onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, customizing]);

  const pin = digits.join('');

  /*
   * Partidas y exámenes comparten el formato de PIN. Con los 6 dígitos puestos
   * se pregunta al servidor si es un examen: entonces sobran el apodo y el
   * avatar (el examen pide nombre y apellido) y el botón lleva a él. Las
   * tareas no tienen PIN: solo se entra con su enlace.
   */
  const [exam, setExam] = useState<ExamPublicInfo | null>(null);
  const [checking, setChecking] = useState(false);
  useEffect(() => {
    setExam(null);
    if (pin.length !== PIN_LENGTH) return;
    let cancelled = false;
    setChecking(true);
    api
      .post<{ exam: ExamPublicInfo }>('/exam-access/lookup', { code: pin })
      .then((d) => !cancelled && setExam(d.exam))
      .catch(() => {
        // 404: no es un examen, así que será una sala. Cualquier otro fallo
        // tampoco debe impedir intentar entrar a la partida.
      })
      .finally(() => !cancelled && setChecking(false));
    return () => {
      cancelled = true;
    };
  }, [pin]);

  const setDigit = (index: number, value: string) => {
    const clean = value.replace(/\D/g, '');
    if (!clean) {
      setDigits((prev) => prev.map((d, i) => (i === index ? '' : d)));
      return;
    }

    // Pegar el PIN completo debe funcionar en cualquier casilla.
    if (clean.length > 1) {
      const chars = clean.slice(0, PIN_LENGTH - index).split('');
      setDigits((prev) => {
        const next = [...prev];
        chars.forEach((c, i) => (next[index + i] = c));
        return next;
      });
      inputsRef.current[Math.min(index + chars.length, PIN_LENGTH - 1)]?.focus();
      return;
    }

    setDigits((prev) => prev.map((d, i) => (i === index ? clean : d)));
    if (index < PIN_LENGTH - 1) inputsRef.current[index + 1]?.focus();
  };

  const onKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0) inputsRef.current[index - 1]?.focus();
    if (e.key === 'ArrowRight' && index < PIN_LENGTH - 1) inputsRef.current[index + 1]?.focus();
  };

  const updateGuest = (patch: Partial<GuestIdentity>) => {
    setGuest((prev) => {
      const next = { ...prev, ...patch };
      saveGuest(next);
      return next;
    });
  };

  // Las mismas reglas que aplica el servidor. Sin comprobarlo aquí, un apodo
  // de dos letras o con un emoji se rechazaba allí en silencio y la persona
  // entraba como "Invitado 1234": en la sala nadie la reconocía.
  const [nameError, setNameError] = useState('');

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (pin.length !== PIN_LENGTH || checking) return;
    if (exam) {
      onExam(pin);
      return;
    }
    if (asGuest && guest.name.trim()) {
      const check = validateUsername(guest.name);
      if (!check.valid) {
        setNameError(check.error ?? 'Apodo no permitido.');
        return;
      }
    }
    onJoin(pin, asGuest ? guest : undefined);
  };

  if (customizing) {
    return (
      <div
        className="fixed inset-0 z-50 bg-ink-950/85 backdrop-blur-sm flex items-start sm:items-center-safe justify-center p-4 overflow-y-auto"
        onMouseDown={(e) => e.target === e.currentTarget && setCustomizing(false)}
        role="dialog"
        aria-modal="true"
        aria-label="Personalizar avatar"
      >
        <div className="w-full max-w-lg my-auto animate-pop">
          <AvatarCustomizer
            initialConfig={guest.avatar}
            onSave={(avatar: AvatarConfig) => {
              updateGuest({ avatar });
              setCustomizing(false);
            }}
            onClose={() => setCustomizing(false)}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-ink-950/85 backdrop-blur-sm flex items-start sm:items-center-safe justify-center p-4 overflow-y-auto"
      onMouseDown={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
      aria-labelledby="join-title"
    >
      <form onSubmit={submit} className="surface-raised bg-ink-900 w-full max-w-[26rem] my-auto animate-pop">
        <header className="flex items-start gap-3 px-6 pt-6 pb-5">
          <AppIcon size={32} />
          <div className="flex-1 min-w-0">
            <h2 id="join-title" className="font-display font-semibold text-[1.0625rem] text-ink-50">
              Entrar con un PIN
            </h2>
            <p className="text-ink-400 text-[0.8125rem] leading-snug mt-0.5">
              El de una partida o el de un examen.
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="-mt-1 -mr-2 p-2 rounded-lg text-ink-500 hover:text-ink-100 hover:bg-ink-800 transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-4.5 h-4.5" />
          </button>
        </header>

        <div className="px-6 pb-6 space-y-5">
          <div className="flex justify-center gap-2" role="group" aria-label="PIN de 6 dígitos">
            {digits.map((digit, i) => (
              <input
                key={i}
                ref={(el) => {
                  inputsRef.current[i] = el;
                }}
                value={digit}
                onChange={(e) => setDigit(i, e.target.value)}
                onKeyDown={(e) => onKeyDown(i, e)}
                onFocus={(e) => e.target.select()}
                inputMode="numeric"
                autoComplete="one-time-code"
                aria-label={`Dígito ${i + 1}`}
                className={`
                  w-11 h-14 sm:w-12 sm:h-16 rounded-lg text-center font-mono text-2xl sm:text-3xl font-bold
                  bg-ink-850 border text-ink-50 outline-none transition-colors
                  ${error ? 'border-danger-400' : digit ? 'border-sky-500' : 'border-ink-700'}
                  focus:border-sky-400 focus:ring-2 focus:ring-sky-500/25
                `}
              />
            ))}
          </div>

          {exam && (
            <div className="flex items-start gap-3 rounded-lg border border-sky-500/40 bg-sky-500/10 px-3 py-2.5">
              <FileText className="w-5 h-5 text-sky-400 shrink-0 mt-0.5" />
              <div className="min-w-0">
                <p className="text-[0.6875rem] uppercase tracking-wider text-sky-400">Examen</p>
                <p className="font-semibold text-ink-50 truncate">{exam.title}</p>
                <p className="text-ink-400 text-[0.8125rem]">
                  {exam.questionCount} preguntas · {exam.durationMin} min
                  {exam.creatorName ? ` · ${exam.creatorName}` : ''}
                </p>
              </div>
            </div>
          )}

          {asGuest && !exam && (
            <div className="space-y-3 pt-1">
              <div className="flex items-center gap-3">
                <span className="h-px flex-1 bg-ink-700" />
                <span className="text-ink-500 text-[0.6875rem] uppercase tracking-wider">
                  Cómo te verán
                </span>
                <span className="h-px flex-1 bg-ink-700" />
              </div>

              <div className="flex items-center gap-3">
                <Avatar config={guest.avatar} size={52} />

                <div className="flex-1 min-w-0">
                  <input
                    value={guest.name}
                    onChange={(e) => {
                      setNameError('');
                      updateGuest({ name: e.target.value.slice(0, 18) });
                    }}
                    maxLength={18}
                    placeholder="Tu apodo"
                    aria-label="Tu apodo"
                    aria-invalid={nameError ? true : undefined}
                    className={`w-full h-10 px-3 rounded-lg bg-ink-850 border ${nameError ? 'border-danger-400' : 'border-ink-700'} text-ink-100 text-sm
                               placeholder:text-ink-600 outline-none transition-colors
                               focus:border-sky-500 focus:ring-2 focus:ring-sky-500/25`}
                  />
                </div>

                <div className="flex gap-1.5 shrink-0">
                  <button
                    type="button"
                    onClick={() => updateGuest({ avatar: randomAvatar() })}
                    className="p-2.5 rounded-lg bg-ink-850 border border-ink-700 text-ink-300
                               hover:text-ink-100 hover:bg-ink-800 transition-colors"
                    aria-label="Otro avatar al azar"
                    title="Otro al azar"
                  >
                    <Dices className="w-4 h-4" />
                  </button>
                  <button
                    type="button"
                    onClick={() => setCustomizing(true)}
                    className="p-2.5 rounded-lg bg-ink-850 border border-ink-700 text-ink-300
                               hover:text-ink-100 hover:bg-ink-800 transition-colors"
                    aria-label="Personalizar avatar"
                    title="Personalizar"
                  >
                    <Pencil className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}

          {(nameError || error) && (
            <p
              role="alert"
              className="flex items-start gap-2 rounded-lg border border-danger-400/30 bg-danger-400/10 px-3 py-2.5 text-danger-400 text-[0.8125rem]"
            >
              <AlertTriangle className="w-4 h-4 shrink-0 mt-px" />
              {nameError || error}
            </p>
          )}

          <button
            type="submit"
            disabled={pin.length !== PIN_LENGTH || checking}
            className="w-full flex items-center justify-center gap-2 h-11 rounded-lg font-semibold
                       bg-ember-500 hover:bg-ember-400 text-on-accent
                       disabled:bg-ink-800 disabled:text-ink-500 disabled:cursor-not-allowed transition-colors"
          >
            {exam ? 'Ir al examen' : 'Entrar'}
            {checking ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
          </button>

          <p className="text-center text-ink-500 text-[0.6875rem] leading-relaxed">
            {exam ? (
              'No hace falta cuenta: en el siguiente paso te pedirá tu nombre y apellido.'
            ) : asGuest ? (
              <>
                No hace falta cuenta para jugar. Usa un apodo, no tu nombre completo. Al entrar
                aceptas los <LegalLink to="terminos">Términos</LegalLink>; tu apodo y tus
                resultados se guardan hasta 12 meses, como explica la{' '}
                <LegalLink to="privacidad">Política de privacidad</LegalLink>.
              </>
            ) : (
              'Jugarás con tu cuenta y tu progreso se guardará.'
            )}
          </p>
        </div>
      </form>
    </div>
  );
};
